import React, { useState, Fragment } from 'react';
import { PlusIcon, XIcon, ArrowRightIcon, SaveIcon, PlayIcon } from 'lucide-react';
export const RecommendationLogicBuilder: React.FC = () => {
  const [rules, setRules] = useState([{
    id: 1,
    conditions: [{
      trait: 'creativity',
      operator: '>=',
      value: 80
    }, {
      trait: 'communication',
      operator: '>=',
      value: 70
    }],
    outcome: {
      career: 'UX Designer',
      weight: 100
    }
  }, {
    id: 2,
    conditions: [{
      trait: 'analytical',
      operator: '>=',
      value: 85
    }, {
      trait: 'technical',
      operator: '>=',
      value: 80
    }],
    outcome: {
      career: 'Data Scientist',
      weight: 100
    }
  }, {
    id: 3,
    conditions: [{
      trait: 'leadership',
      operator: '>=',
      value: 75
    }, {
      trait: 'communication',
      operator: '>=',
      value: 80
    }, {
      trait: 'analytical',
      operator: '>=',
      value: 70
    }],
    outcome: {
      career: 'Product Manager',
      weight: 100
    }
  }]);
  const [newRule, setNewRule] = useState({
    conditions: [{
      trait: 'creativity',
      operator: '>=',
      value: 70
    }],
    outcome: {
      career: '',
      weight: 100
    }
  });
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const traits = ['creativity', 'analytical', 'communication', 'leadership', 'technical', 'adaptability', 'empathy'];
  const careers = ['UX Designer', 'Data Scientist', 'Product Manager', 'Marketing Specialist', 'Software Engineer', 'HR Manager', 'Financial Analyst'];
  const operators = ['>=', '<=', '=', '>', '<'];
  const addCondition = () => {
    setNewRule({
      ...newRule,
      conditions: [...newRule.conditions, {
        trait: traits[0],
        operator: '>=',
        value: 70
      }]
    });
  };
  const removeCondition = (index: number) => {
    const updatedConditions = [...newRule.conditions];
    updatedConditions.splice(index, 1);
    setNewRule({
      ...newRule,
      conditions: updatedConditions
    });
  };
  const updateCondition = (index: number, field: string, value: string | number) => {
    const updatedConditions = [...newRule.conditions];
    updatedConditions[index] = {
      ...updatedConditions[index],
      [field]: field === 'value' ? Number(value) : value
    };
    setNewRule({
      ...newRule,
      conditions: updatedConditions
    });
  };
  const updateOutcome = (field: string, value: string | number) => {
    setNewRule({
      ...newRule,
      outcome: {
        ...newRule.outcome,
        [field]: field === 'weight' ? Number(value) : value
      }
    });
  };
  const saveRule = () => {
    setRules([...rules, {
      id: rules.length + 1,
      ...newRule
    }]);
    setNewRule({
      conditions: [{
        trait: 'creativity',
        operator: '>=',
        value: 70
      }],
      outcome: {
        career: '',
        weight: 100
      }
    });
    setIsCreatingRule(false);
  };
  const deleteRule = (id: number) => {
    setRules(rules.filter(rule => rule.id !== id));
  };
  const previewLogic = () => {
    // Mock user profile for preview
    const userProfile = {
      creativity: 85,
      analytical: 90,
      communication: 75,
      leadership: 60,
      technical: 80,
      adaptability: 70,
      empathy: 65
    };
    const matches = rules.filter(rule => {
      return rule.conditions.every(condition => {
        const userValue = userProfile[condition.trait as keyof typeof userProfile];
        switch (condition.operator) {
          case '>=':
            return userValue >= condition.value;
          case '<=':
            return userValue <= condition.value;
          case '=':
            return userValue === condition.value;
          case '>':
            return userValue > condition.value;
          case '<':
            return userValue < condition.value;
          default:
            return false;
        }
      });
    }).map(rule => rule.outcome);
    setPreviewResult({
      userProfile,
      matches
    });
  };
  return <div data-id="element-368">
      <div className="mb-6" data-id="element-369">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-370">
          Recommendation Logic Builder
        </h1>
        <p className="text-gray-600" data-id="element-371">
          Create rules to match users with careers based on their trait scores
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-372">
        <div className="lg:col-span-2" data-id="element-373">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-374">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center" data-id="element-375">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-376">
                Recommendation Rules
              </h2>
              <button onClick={() => setIsCreatingRule(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#7D56F4] hover:bg-[#6A46E5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7D56F4]" data-id="element-377">
                <PlusIcon size={16} className="mr-2" data-id="element-378" />
                New Rule
              </button>
            </div>
            <div className="p-6 space-y-6" data-id="element-379">
              {rules.map(rule => <div key={rule.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-380">
                  <div className="flex justify-between items-start" data-id="element-381">
                    <div data-id="element-382">
                      <h3 className="font-medium text-gray-900 mb-2" data-id="element-383">
                        Rule #{rule.id}: {rule.outcome.career}
                      </h3>
                      <div className="space-y-2" data-id="element-384">
                        <div className="flex items-center text-sm" data-id="element-385">
                          <span className="text-gray-500 mr-2" data-id="element-386">IF</span>
                          {rule.conditions.map((condition, index) => <Fragment key={index} data-id="element-387">
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium" data-id="element-388">
                                {condition.trait} {condition.operator}{' '}
                                {condition.value}
                              </span>
                              {index < rule.conditions.length - 1 && <span className="mx-2 text-gray-500" data-id="element-389">AND</span>}
                            </Fragment>)}
                        </div>
                        <div className="flex items-center text-sm" data-id="element-390">
                          <span className="text-gray-500 mr-2" data-id="element-391">THEN</span>
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium" data-id="element-392">
                            Recommend {rule.outcome.career} (Weight:{' '}
                            {rule.outcome.weight})
                          </span>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => deleteRule(rule.id)} className="text-gray-400 hover:text-red-500" data-id="element-393">
                      <XIcon size={16} data-id="element-394" />
                    </button>
                  </div>
                </div>)}
              {rules.length === 0 && <div className="text-center py-8 text-gray-500" data-id="element-395">
                  No rules created yet. Click "New Rule" to create your first
                  recommendation rule.
                </div>}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-396">
              <button onClick={previewLogic} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#7D56F4] hover:bg-[#6A46E5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7D56F4]" data-id="element-397">
                <PlayIcon size={16} className="mr-2" data-id="element-398" />
                Preview Logic
              </button>
            </div>
          </div>
          {isCreatingRule && <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-399">
              <div className="p-6 border-b border-gray-200" data-id="element-400">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-401">
                  Create New Rule
                </h2>
              </div>
              <div className="p-6" data-id="element-402">
                <div className="mb-6" data-id="element-403">
                  <h3 className="font-medium text-gray-900 mb-3" data-id="element-404">
                    Conditions (IF)
                  </h3>
                  {newRule.conditions.map((condition, index) => <div key={index} className="flex items-center mb-3" data-id="element-405">
                      <select value={condition.trait} onChange={e => updateCondition(index, 'trait', e.target.value)} className="border border-gray-300 rounded-md py-2 px-3 mr-2 focus:outline-none focus:ring-2 focus:ring-[#7D56F4] focus:border-[#7D56F4]" data-id="element-406">
                        {traits.map(trait => <option key={trait} value={trait} data-id="element-407">
                            {trait}
                          </option>)}
                      </select>
                      <select value={condition.operator} onChange={e => updateCondition(index, 'operator', e.target.value)} className="border border-gray-300 rounded-md py-2 px-3 mr-2 focus:outline-none focus:ring-2 focus:ring-[#7D56F4] focus:border-[#7D56F4]" data-id="element-408">
                        {operators.map(op => <option key={op} value={op} data-id="element-409">
                            {op}
                          </option>)}
                      </select>
                      <input type="number" min="0" max="100" value={condition.value} onChange={e => updateCondition(index, 'value', e.target.value)} className="border border-gray-300 rounded-md py-2 px-3 mr-2 w-20 focus:outline-none focus:ring-2 focus:ring-[#7D56F4] focus:border-[#7D56F4]" data-id="element-410" />
                      {newRule.conditions.length > 1 && <button onClick={() => removeCondition(index)} className="text-gray-400 hover:text-red-500" data-id="element-411">
                          <XIcon size={16} data-id="element-412" />
                        </button>}
                    </div>)}
                  <button onClick={addCondition} className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-413">
                    <PlusIcon size={14} className="mr-1" data-id="element-414" />
                    Add Condition
                  </button>
                </div>
                <div data-id="element-415">
                  <h3 className="font-medium text-gray-900 mb-3" data-id="element-416">
                    Outcome (THEN)
                  </h3>
                  <div className="flex items-center" data-id="element-417">
                    <div className="mr-2" data-id="element-418">Recommend</div>
                    <select value={newRule.outcome.career} onChange={e => updateOutcome('career', e.target.value)} className="border border-gray-300 rounded-md py-2 px-3 mr-2 focus:outline-none focus:ring-2 focus:ring-[#7D56F4] focus:border-[#7D56F4]" data-id="element-419">
                      <option value="" data-id="element-420">Select a career</option>
                      {careers.map(career => <option key={career} value={career} data-id="element-421">
                          {career}
                        </option>)}
                    </select>
                    <div className="mr-2" data-id="element-422">with weight</div>
                    <input type="number" min="1" max="100" value={newRule.outcome.weight} onChange={e => updateOutcome('weight', e.target.value)} className="border border-gray-300 rounded-md py-2 px-3 w-20 focus:outline-none focus:ring-2 focus:ring-[#7D56F4] focus:border-[#7D56F4]" data-id="element-423" />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3" data-id="element-424">
                <button onClick={() => setIsCreatingRule(false)} className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-425">
                  Cancel
                </button>
                <button onClick={saveRule} disabled={!newRule.outcome.career} className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${newRule.outcome.career ? 'bg-[#7D56F4] hover:bg-[#6A46E5]' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-426">
                  <SaveIcon size={16} className="mr-2" data-id="element-427" />
                  Save Rule
                </button>
              </div>
            </div>}
        </div>
        <div data-id="element-428">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-429">
            <div className="p-6 border-b border-gray-200" data-id="element-430">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-431">
                Preview Results
              </h2>
            </div>
            <div className="p-6" data-id="element-432">
              {previewResult ? <div data-id="element-433">
                  <h3 className="font-medium text-gray-900 mb-3" data-id="element-434">
                    Sample User Profile
                  </h3>
                  <div className="space-y-2 mb-6" data-id="element-435">
                    {Object.entries(previewResult.userProfile).map(([trait, value]) => <div key={trait} className="flex items-center" data-id="element-436">
                          <span className="text-sm w-32 capitalize" data-id="element-437">
                            {trait}
                          </span>
                          <div className="flex-1" data-id="element-438">
                            <div className="h-2 bg-gray-200 rounded-full" data-id="element-439">
                              <div className="h-2 bg-[#7D56F4] rounded-full" style={{
                        width: `${value}%`
                      }} data-id="element-440"></div>
                            </div>
                          </div>
                          <span className="text-sm w-8 text-right" data-id="element-441">
                            {value}%
                          </span>
                        </div>)}
                  </div>
                  <h3 className="font-medium text-gray-900 mb-3" data-id="element-442">
                    Matched Careers
                  </h3>
                  {previewResult.matches.length > 0 ? <div className="space-y-2" data-id="element-443">
                      {previewResult.matches.map((match: any, index: number) => <div key={index} className="flex items-center bg-green-50 border border-green-200 rounded-md p-3" data-id="element-444">
                            <ArrowRightIcon size={16} className="text-green-500 mr-2" data-id="element-445" />
                            <div data-id="element-446">
                              <div className="font-medium" data-id="element-447">{match.career}</div>
                              <div className="text-xs text-gray-500" data-id="element-448">
                                Weight: {match.weight}
                              </div>
                            </div>
                          </div>)}
                    </div> : <div className="text-center py-4 text-gray-500" data-id="element-449">
                      No career matches found for this profile
                    </div>}
                </div> : <div className="text-center py-8 text-gray-500" data-id="element-450">
                  Click "Preview Logic" to see how your rules would match with a
                  sample user profile
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};